// src/features/practitioner/import/ResultStep.jsx
import { useAppContext } from "providers/AppProvider";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { genderData, nationalityData, specilaityData } from "../utils";
import React, { useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import {
  updateDuplicateRow,
  deleteDuplicateRow,
  updateRowData,
} from "../config/practitionerSlice";
import EditPractitionerModal from "./EditPractitionerModal";
import IconButton from "components/common/IconButton";
import { Pen, Trash } from "react-bootstrap-icons";

const ResultStep = () => {
  const dispatch = useDispatch();
  const { tableData, duplicateRows } = useSelector(
    (state) => state.practitioner
  );
  const {
    config: { theme },
  } = useAppContext();

  const [editingRow, setEditingRow] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nationalId: "",
    passportNo: "",
  });

  // Handle edit button click
  const handleEditClick = (row) => {
    setSelectedRow(row);

    setShowModal(true);
  };

  // Handle save edited row
  const handleSaveEdit = () => {
    dispatch(
      updateDuplicateRow({
        rowNum: editingRow.rowNum,
        updatedData: {
          nationalId: editFormData.nationalId,
          passportNo: editFormData.passportNo,
        },
      })
    );
    setShowEditModal(false);
  };

  // Handle delete row
  const handleDeleteRow = (rowNum) => {
    dispatch(deleteDuplicateRow(rowNum));
  };

  const columns = [
    {
      name: "#",
      selector: (row) => row.rowNum - 1,
      width: "60px",
    },
    {
      name: "License No",
      selector: (row) => row["License No"] || "-",
    },
    {
      name: "Name",
      selector: (row) =>
        `${row["First Name (Eng)"] || ""} ${
          row["Last Name (Eng)"] || ""
        }`.trim(),
    },
    {
      name: "Speciality",
      selector: (row) => {
        const raw = row["Speciality"] || row["speciality"];

        if (Array.isArray(raw)) {
          // Case: [ { value, label }, ... ]
          return raw.map((s) => s.label || s.title).join(", ");
        }

        if (typeof raw === "object" && raw !== null) {
          // Case: { value, label }
          return raw.label || raw.title || "-";
        }

        if (typeof raw === "string") {
          // Case: "01.00,01.02"
          const codes = raw.split(",").map((code) => code.trim());
          const labels = codes
            .map((code) => {
              const match = specilaityData.find((item) => item.value === code);
              return match?.label || code;
            })
            .filter(Boolean);
          return labels.join(", ");
        }

        return "-";
      },
      sortable: true,
    },

    {
      name: "Gender",
      sortable: true,
      selector: (row) => (
        <span className="text-capitalize">
          {genderData?.find((item) => item?.value === row?.["Gender"])?.title ||
            row?.["Gender"] ||
            ""}
        </span>
      ),
    },
    {
      name: "Nationality",
      selector: (row) =>
        nationalityData?.find((item) => item?.value === row["Nationality"])
          ?.title ||
        row["Nationality"] ||
        "-",
    },
    {
      name: "Status",
      cell: (row) => {
        if (row.status === "duplicate") {
          return <span className="badge bg-warning">Duplicate</span>;
        }
        return row.isValid ? (
          <span className="badge bg-success">Valid</span>
        ) : (
          <span className="badge bg-danger">Invalid</span>
        );
      },
      width: "100px",
    },
  ];

  const updateRowDataRow = (rowNum, updatedData, isValid) => {
    dispatch(updateRowData({ rowNum, updatedData, isValid }));
  };

  const duplicateColumns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      width: "60px",
    },
    {
      name: "License No",
      selector: (row) => row["License No"] || "-",
    },
    {
      name: "Name",
      selector: (row) =>
        `${row["First Name (Eng)"] || ""} ${
          row["Last Name (Eng)"] || ""
        }`.trim(),
    },
    {
      name: "Speciality",
      selector: (row) => {
        const raw = row["Speciality"] || row["speciality"];

        if (Array.isArray(raw)) {
          // Case: [ { value, label }, ... ]
          return raw.map((s) => s.label || s.title).join(", ");
        }

        if (typeof raw === "object" && raw !== null) {
          // Case: { value, label }
          return raw.label || raw.title || "-";
        }

        if (typeof raw === "string") {
          // Case: "01.00,01.02"
          const codes = raw.split(",").map((code) => code.trim());
          const labels = codes
            .map((code) => {
              const match = specilaityData.find((item) => item.value === code);
              return match?.label || code;
            })
            .filter(Boolean);
          return labels.join(", ");
        }

        return "-";
      },
      sortable: true,
    },

    {
      name: "Gender",
      sortable: true,
      selector: (row) => (
        <span className="text-capitalize">
          {genderData?.find((item) => item?.value === row?.["Gender"])?.title ||
            row?.["Gender"] ||
            ""}
        </span>
      ),
    },
    {
      name: "Nationality",
      selector: (row) =>
        nationalityData?.find((item) => item?.value === row["Nationality"])
          ?.title ||
        row["Nationality"] ||
        "-",
    },

    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex gap-2">
          <IconButton
            size="sm"
            variant="outline-primary"
            onClick={() => handleEditClick(row)}
          >
            <Pen />
          </IconButton>
          <IconButton
            size="sm"
            variant="outline-danger"
            onClick={() => handleDeleteRow(row.rowNum)}
          >
            <Trash />
          </IconButton>
        </div>
      ),
      width: "150px",
    },
  ];

  return (
    <div className="">
      {duplicateRows?.length > 0 && (
        <div className="mb-4">
          <h5 className="text-danger mb-3">
            Duplicate Records Found ({duplicateRows.length})
          </h5>
          <DataTable
            columns={duplicateColumns}
            data={duplicateRows}
            theme={theme}
            highlightOnHover
            dense
          />
        </div>
      )}

      <DataTable
        columns={columns}
        data={tableData}
        pagination
        theme={theme}
        highlightOnHover
        dense
      />

      {/* Edit Modal */}
      <EditPractitionerModal
        show={showModal}
        onHide={() => setShowModal(false)}
        rowData={selectedRow}
        updateRowData={updateRowDataRow}
        setSelectedRow={setSelectedRow}
      />
    </div>
  );
};

export default ResultStep;
